import { Component, computed, inject, input, output, signal } from '@angular/core';
import { BuildingBadgeDto, MaintenanceTimerDetailDto, MaintenanceTimerFilterDto, MaintenanceTimerService } from '@artemis-software/wr-api';
import { asyncComputed } from '@/utils/signals/asyncComputed';
import { watch } from '@/utils/signals/watch';
import { CurrentUserService } from '@services/current-user.service';

@Component({
  standalone: false,
  selector: 'wr-maintenance-timer-dashboard-list',
  templateUrl: './maintenance-timer-dashboard-list.component.html',
  styleUrls: ['./maintenance-timer-dashboard-list.component.scss'],
})
export class MaintenanceTimerDashboardListComponent {
  readonly maintenanceTimerService = inject(MaintenanceTimerService);
  readonly currentUserService = inject(CurrentUserService);

  readonly canCreate = computed(() => this.currentUserService.hasPrivilege('canCreateMaintenanceTimers'));

  readonly filter = input<MaintenanceTimerFilterDto | undefined>();

  readonly addMaintenanceTimer = output<{ buildingId: string }>();

  readonly refreshSignal = signal(0);
  readonly isLoading = signal(false);
  readonly loadError = signal(false);

  readonly maintenanceTimers = asyncComputed(
    () => {
      this.refreshSignal();
      this.loadError.set(false);
      const filter = this.filter();
      if (!filter) return [];
      return this.maintenanceTimerService.findAllDetailDtos({
        ...filter,
        sort: {
          sort: 'nextDate',
          order: 'ASC',
        },
      });
    },
    [] as MaintenanceTimerDetailDto[],
    {
      isLoading: this.isLoading,
      onError: () => this.loadError.set(true),
    },
  );

  readonly maintenanceTimersGrouped = computed(() => {
    return this.maintenanceTimers().reduce((acc, maintenanceTimer) => {
      const key = maintenanceTimer.building?.id;
      if (!key) return acc;
      if (!acc[key]) {
        acc[key] = [] as MaintenanceTimerDetailDto[];
      }
      acc[key].push(maintenanceTimer);
      return acc;
    }, {} as Record<string, MaintenanceTimerDetailDto[]>);
  });

  readonly buildings = computed(() => {
    const seen = new Set<string>();
    return this.maintenanceTimers()
      .map(t => t.building)
      .filter((b): b is BuildingBadgeDto => !!b && (seen.has(b.id) ? false : !!seen.add(b.id)));
  });

  readonly showNoResults = computed(() => !this.isLoading() && this.maintenanceTimers().length === 0);

  constructor() {
    // Set loading eagerly the moment the filter or refresh changes, before the
    // asyncComputed effect runs — otherwise fast HTTP responses complete before
    // Angular renders the spinner.
    watch(
      () => [this.filter(), this.refreshSignal()] as const,
      ([filter]) => {
        if (filter) {
          this.isLoading.set(true);
        }
      },
    );
  }

  refresh() {
    this.refreshSignal.set(this.refreshSignal() + 1);
  }

  onUpdate(maintenanceTimer: MaintenanceTimerDetailDto) {
    const maintenanceTimers = [...this.maintenanceTimers()];
    const index = maintenanceTimers.findIndex(t => t.id === maintenanceTimer.id);
    if (index >= 0) {
      maintenanceTimers[index] = maintenanceTimer;
      // The list is loaded sorted by nextDate ASC — keep that order after a
      // local replace (a finished maintenance or changed duration moves nextDate).
      maintenanceTimers.sort((a, b) => a.nextDate.localeCompare(b.nextDate));
      this.maintenanceTimers.set(maintenanceTimers);
    }
  }

  onDelete(maintenanceTimer: MaintenanceTimerDetailDto) {
    this.maintenanceTimers.set(this.maintenanceTimers().filter(t => t.id !== maintenanceTimer.id));
  }
}
